import { defineAsyncComponent, hydrateOnVisible } from 'vue'
import type { ADD_BLOCKS, CHILDS_TO_ADD } from '@@/shared-components/types/Blocks'
import { DEFAULT_ICON_CARD_DATA, DEFAULT_ICON_CARD_PARAMS } from './blocks/IconCard/const'
import type { ICON_CARD_DATA, ICON_CARD_PARAMS } from './blocks/IconCard/types'
import { DEFAULT_PROJECT_PREVIEW_DATA, DEFAULT_PROJECT_PREVIEW_PARAMS } from './blocks/ProjectPreview/const'
import type { PROJECT_PREVIEW_DATA, PROJECT_PREVIEW_PARAMS } from './blocks/ProjectPreview/types'
import { DEFAULT_FEATURE_GRID_DATA, DEFAULT_FEATURE_GRID_PARAMS } from './blocks/FeatureGrid/const'
import type { FEATURE_GRID_DATA, FEATURE_GRID_PARAMS } from './blocks/FeatureGrid/types'
import { DEFAULT_FOUNDER_DATA, DEFAULT_FOUNDER_PARAMS } from './blocks/Founder/const'
import type { FOUNDER_DATA, FOUNDER_PARAMS } from './blocks/Founder/types'
import { DEFAULT_PRICING_DATA, DEFAULT_PRICING_PARAMS } from './blocks/Pricing/const'
import type { PRICING_DATA, PRICING_PARAMS } from './blocks/Pricing/types'
import { DEFAULT_CTA_BAND_DATA, DEFAULT_CTA_BAND_PARAMS } from './blocks/CtaBand/const'
import type { CTA_BAND_DATA, CTA_BAND_PARAMS } from './blocks/CtaBand/types'
import { DEFAULT_CONTENTS } from '@@/shared-components/content/const'

// Project block registry. Consumed by the web client plugin AND the admin editor module — both call
// Blocks.getInstance().addBlocks(BLOCKS()) etc. Each block is a SECTION: styling lives in its View,
// the CMS edit tab holds content only (texts, images, repeatable items).
export const BLOCKS = (): ADD_BLOCKS => ({
  'feature-grid': {
    type: 'block',
    params: DEFAULT_FEATURE_GRID_PARAMS(),
    data: DEFAULT_FEATURE_GRID_DATA(),
    contents: DEFAULT_CONTENTS(),
    properties: { additionalDocuments: ['image'] },
    components: {
      edit: defineAsyncComponent(() => import('./blocks/FeatureGrid/FeatureGridEdit.vue')),
      view: defineAsyncComponent({ loader: () => import('./blocks/FeatureGrid/FeatureGridView.vue'), hydrate: hydrateOnVisible() }),
      icon: defineAsyncComponent(() => import('./blocks/FeatureGrid/FeatureGridIcon.vue')),
    },
  },
  'project-preview': {
    type: 'block',
    params: DEFAULT_PROJECT_PREVIEW_PARAMS(),
    data: DEFAULT_PROJECT_PREVIEW_DATA(),
    interactions: {},
    contents: DEFAULT_CONTENTS(),
    components: {
      edit: defineAsyncComponent(() => import('./blocks/ProjectPreview/ProjectPreviewEdit.vue')),
      // No lazy hydration (hydrateOnVisible): this view fetches its data with useAsyncData, and lazy
      // hydration prevents the blocking async setup from running during SSR (cards would be missing
      // from the server HTML). Plain async component still code-splits but SSR-fetches + hydrates.
      view: defineAsyncComponent(() => import('./blocks/ProjectPreview/ProjectPreviewView.vue')),
      icon: defineAsyncComponent(() => import('./blocks/ProjectPreview/ProjectPreviewIcon.vue')),
    },
  },
  founder: {
    type: 'block',
    params: DEFAULT_FOUNDER_PARAMS(),
    data: DEFAULT_FOUNDER_DATA(),
    contents: DEFAULT_CONTENTS(),
    properties: { additionalDocuments: ['data'] },
    components: {
      edit: defineAsyncComponent(() => import('./blocks/Founder/FounderEdit.vue')),
      view: defineAsyncComponent({ loader: () => import('./blocks/Founder/FounderView.vue'), hydrate: hydrateOnVisible() }),
      icon: defineAsyncComponent(() => import('./blocks/Founder/FounderIcon.vue')),
    },
  },
  pricing: {
    type: 'block',
    params: DEFAULT_PRICING_PARAMS(),
    data: DEFAULT_PRICING_DATA(),
    components: {
      edit: defineAsyncComponent(() => import('./blocks/Pricing/PricingEdit.vue')),
      view: defineAsyncComponent({ loader: () => import('./blocks/Pricing/PricingView.vue'), hydrate: hydrateOnVisible() }),
      icon: defineAsyncComponent(() => import('./blocks/Pricing/PricingIcon.vue')),
    },
  },
  'cta-band': {
    type: 'block',
    params: DEFAULT_CTA_BAND_PARAMS(),
    data: DEFAULT_CTA_BAND_DATA(),
    contents: DEFAULT_CONTENTS(),
    properties: { additionalDocuments: ['data'] },
    components: {
      edit: defineAsyncComponent(() => import('./blocks/CtaBand/CtaBandEdit.vue')),
      view: defineAsyncComponent({ loader: () => import('./blocks/CtaBand/CtaBandView.vue'), hydrate: hydrateOnVisible() }),
      icon: defineAsyncComponent(() => import('./blocks/CtaBand/CtaBandIcon.vue')),
    },
  },
  // Standalone single card (also used internally by feature-grid).
  'icon-card': {
    type: 'block',
    params: DEFAULT_ICON_CARD_PARAMS(),
    data: DEFAULT_ICON_CARD_DATA(),
    interactions: {},
    components: {
      edit: defineAsyncComponent(() => import('./blocks/IconCard/IconCardEdit.vue')),
      view: defineAsyncComponent({ loader: () => import('./blocks/IconCard/IconCardView.vue'), hydrate: hydrateOnVisible() }),
      icon: defineAsyncComponent(() => import('./blocks/IconCard/IconCardIcon.vue')),
    },
  },
})

// Restyle built-in block views (overwriteBlock) — FAQ → MD Club design.
export const OVERWRITES = () => ({
  faq: defineAsyncComponent({ loader: () => import('./blocks/FAQ/FAQView.vue'), hydrate: hydrateOnVisible() }),
})

// Hero = the built-in `slider` block restyled: overwrite the `general-slide` child view.
export const CHILD_COMPONENTS = () => ({
  slider: {
    'general-slide': {
      view: defineAsyncComponent({ loader: () => import('./blocks/Hero/HeroSlideView.vue'), hydrate: hydrateOnVisible() }),
    },
  },
})

declare module '@@/shared-components/types/Blocks' {
  interface BlockDataRegistry {
    'icon-card': ICON_CARD_DATA
    'project-preview': PROJECT_PREVIEW_DATA
    'feature-grid': FEATURE_GRID_DATA
    founder: FOUNDER_DATA
    pricing: PRICING_DATA
    'cta-band': CTA_BAND_DATA
  }
  interface BlockParamsRegistry {
    'icon-card': ICON_CARD_PARAMS
    'project-preview': PROJECT_PREVIEW_PARAMS
    'feature-grid': FEATURE_GRID_PARAMS
    founder: FOUNDER_PARAMS
    pricing: PRICING_PARAMS
    'cta-band': CTA_BAND_PARAMS
  }
  interface AdditionalBlockChildsRegistry {}
  interface BlockStylesRegistry {}
}

export const BLOCKS_CHILDS = (): {
  [key in keyof BlockChildsRegistry]?: {
    [childKey in BlockChildsRegistry[key]]: CHILDS_TO_ADD
  }
} => ({})

export const BLOCKS_CHILDS_COMPONENTS = (): {
  [key in keyof BlockChildsRegistry]?: {
    [childKey in BlockChildsRegistry[key]]?: {
      view?: Component
      edit?: Component
      inline?: Component
      icon?: Component
    }
  }
} => ({})

export const ADVANCED_BLOCKS = (): { [key in AdvancedBlocks]?: Component } => ({})

export const STYLES = (): { [key in keyof BlockDataRegistry]?: BLOCKS_STYLES<key> } => ({})

export const ICONS = () => ({})

export const LABELS = () => {
  const {
    $i18n: { t },
  } = useNuxtApp()
  return {
    'icon-card': t('Karta s ikonou'),
    'project-preview': t('Náhled realizací'),
    'feature-grid': t('Sekce s ikonami'),
    founder: t('Zakladatelka'),
    pricing: t('Členství / cena'),
    'cta-band': t('Závěrečná výzva'),
  }
}

export const COLORS = () => [
  '#000000',
  '#1d1b18',
  '#776d64',
  '#4b443d',
  '#989189',
  '#eae9e5',
  '#f3f2ee',
  '#ffffff',
]

export const FONT_SIZES = () => ({
  '0px': { value: 14, unit: 'px' },
  '768px': { value: 15, unit: 'px' },
  '1536px': { value: 16, unit: 'px' },
})
